import React, { useEffect, useMemo } from "react";
import type { U2104OrganizationFormExtendedHandlers } from "../../../U2104OrganizationForm";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { NS, U2104ROLES } from "../../../../U2104";
import { useTranslation } from "react-i18next";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useU2104Context } from "../../../../U2104Context";
import type { useU2000LUStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000LUHandlers";
import { useNavigate } from "react-router-dom";
import { doDeleteAffiliationData, luHandleAppiError, type U2104Affiliation } from "../../../../U2104Service";
import { useU2000DeleteHandlers } from "../../../../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";
import U2104OrganizationAffiliaitionDeleteDlg from "../../../common/U2104OrganizationAffiliationDeleteDlg";
import { u2104OrganizationAffiliationHandler } from "./U2104OrganizationAffiliationHandlers";

// Typage des props du composant
interface U2104OrganizationAffiliationProps {
  u2104OrganizationFormHandlers: U2104OrganizationFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "username",
      label: t("user.lblUserName", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "firstName",
      label: t("user.lblFirstName", { ns: NS }),
      widthClass: "w-[20%]",
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "lastName",
      label: t("user.lblLastName", { ns: NS }),
      widthClass: "w-[20%]",
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "email",
      label: t("user.lblEmail", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "active",
      label: t("user.lblChkActive", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, value: string, _row: any) => {
        return value ? <span className="font-bold text-green-600">{t("valActive", { ns: NS })}</span> : <span className="font-bold text-red-600">{t("valNotActive", { ns: NS })}</span>;
      },
    },
  ];
}

// Composant principal pour la gestion des affiliations d'une unité logique
const U2104OrganizationAffiliationBody: React.FC<U2104OrganizationAffiliationProps> = ({ u2104OrganizationFormHandlers: u2104OrganizationFormSHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const navigate = useNavigate();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const { luStateHandlers, gridOrgAffiliationStateHandlers: gridOrgUserStateHandlers } = useU2104Context();

  // Handlers pour les actions d'affiliation
  const u2104OrganizationAffiliationHandlers = u2104OrganizationAffiliationHandler({
    u2000AxiosAuthenticated,
    luStateHandlers: luStateHandlers as unknown as ReturnType<typeof useU2000LUStateHandlers>,
    gridStateHandlers: gridOrgUserStateHandlers as unknown as ReturnType<typeof useU2000GridStateHandlers<U2104Affiliation>>,
    t,
    navigate,
    organizationId: u2104OrganizationFormSHandlers.getData?.()?.id ?? "",
  });

  // Définition des colonnes du tableau
  const columns = useMemo(() => getColumnsDef(t), [t]);

  // Mettre à jour le gridStateHandlers lorsque le formStateHandlers parent change
  useEffect(() => {
    // Mettre à jour les colonnes seulement si nécessaire (évite boucle de rendu)
    try {
      const currentColumns = gridOrgUserStateHandlers.getColumns?.() ?? [];
      if (JSON.stringify(currentColumns) !== JSON.stringify(columns)) {
        gridOrgUserStateHandlers.setSize(9999);
        gridOrgUserStateHandlers.setColumns(columns);
        gridOrgUserStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
        gridOrgUserStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
      }

      // Initialiser les données du tableau seulement si nécessaire
      const users = (u2104OrganizationFormSHandlers.getData?.()?.users as U2104Affiliation[]) ?? [];
      const currentData = gridOrgUserStateHandlers.getData?.() ?? [];
      if (JSON.stringify(currentData) !== JSON.stringify(users)) {
        gridOrgUserStateHandlers.setData(Array.isArray(users) ? users : []);
        gridOrgUserStateHandlers.setTotalRows(Array.isArray(users) ? users.length : 0);
      }

      // Gérer les erreurs spécifiques au champ LogicalUnitActions
      const errorsField = u2104OrganizationFormSHandlers.getOtherErrors()?.roleActions;
      const normalizedErrors = errorsField !== undefined ? (Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []) : [];
      const currentErrors = gridOrgUserStateHandlers.getErrors?.() ?? [];
      if (JSON.stringify(currentErrors) !== JSON.stringify(normalizedErrors)) {
        gridOrgUserStateHandlers.setErrors(normalizedErrors);
      }
    } catch (e) {
      // En cas d'erreur de sérialisation, fallback to direct set to avoid bloquer l'UI
      const cols = columns;
      gridOrgUserStateHandlers.setColumns(cols);
      gridOrgUserStateHandlers.setSize(9999);
      const organizationUsers = (u2104OrganizationFormSHandlers.getData?.()?.users as U2104Affiliation[]) ?? [];
      gridOrgUserStateHandlers.setData(Array.isArray(organizationUsers) ? organizationUsers : []);
      gridOrgUserStateHandlers.setTotalRows(Array.isArray(organizationUsers) ? organizationUsers.length : 0);
      const errorsField = u2104OrganizationFormSHandlers.getOtherErrors()?.roleActions;
      gridOrgUserStateHandlers.setErrors(errorsField !== undefined ? (Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []) : []);
    }
  }, [u2104OrganizationFormSHandlers.getData(), t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u2104OrganizationAffiliationHandlers.dataChangedEvent === 0) return;

    const organizationUsers = gridOrgUserStateHandlers.getData();
    u2104OrganizationFormSHandlers.setData({
      ...u2104OrganizationFormSHandlers.getData?.(),
      users: organizationUsers,
    });
  }, [u2104OrganizationAffiliationHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridOrgUserStateHandlers.getSelectedRows();
    return u2104OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridOrgUserStateHandlers.getSelectedRows();
    return u2104OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || selectedRows.length === 0 ? true : false;
  }

  // Gestion des Delete Record avec confirmation
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridOrgUserStateHandlers ? gridOrgUserStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (userId, row) => {
      return await doDeleteAffiliationData(u2000AxiosAuthenticated, u2104OrganizationFormSHandlers.getData().id, userId, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridOrgUserStateHandlers.getData();
      let totalRows = gridOrgUserStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: (row as U2104Affiliation)?.user?.username ?? "", ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });

      gridOrgUserStateHandlers.setData(newData);
      gridOrgUserStateHandlers.setTotalRows(totalRows);
      u2104OrganizationAffiliationHandlers.setDataChangedEvent(u2104OrganizationAffiliationHandlers.dataChangedEvent + 1);
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "import",
        label: t("btnImportAffiliation", { ns: NS }),
        handleClick: u2104OrganizationAffiliationHandlers.onImport,
        hasRole: hasRole(U2104ROLES.USERS),
        disabled: u2104OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2104OrganizationAffiliationHandlers.onAdd,
        hasRole: hasRole(U2104ROLES.USERS),
        disabled: u2104OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u2104OrganizationAffiliationHandlers.onModify,
        hasRole: hasRole(U2104ROLES.USERS),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handleDelete.handleDelete,
        hasRole: hasRole(U2104ROLES.USERS),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2104OrganizationAffiliationHandlers]
  );

  // Render
  return (
    <>
      <U2104OrganizationAffiliaitionDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridOrgUserStateHandlers}
        rowClickFn={u2104OrganizationAffiliationHandlers.onRowClick as unknown as (row: any) => void}
        allowRowSelection={u2104OrganizationFormSHandlers.getFormState?.() !== U2000FormStateEnumState.EDIT}
        label={t("lblUser", {
          count: u2104OrganizationAffiliationHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2104OrganizationAffiliationBody;
